import { useEffect, useMemo, useRef, useState } from 'react'

type Align = 'left' | 'right'

interface BaseProps {
  label: string
  align?: Align
  /** Direção de ordenação ativa nessa coluna; undefined = não está ordenando por ela. */
  sortDir?: 'asc' | 'desc'
  onSort?: () => void
}

interface CategoricalProps extends BaseProps {
  kind: 'categorical'
  options: string[]
  /** Vazio = "todos". */
  selected: Set<string>
  onChange: (next: Set<string>) => void
}

interface NumericProps extends BaseProps {
  kind: 'numeric'
  min: number | null
  max: number | null
  onChange: (min: number | null, max: number | null) => void
  /** Casas decimais para placeholder/display. */
  step?: number
}

interface DateProps extends BaseProps {
  kind: 'date'
  /** ISO yyyy-mm-dd, ou null. */
  from: string | null
  to: string | null
  onChange: (from: string | null, to: string | null) => void
}

type Props = CategoricalProps | NumericProps | DateProps

export default function ColumnHeaderFilter(props: Props) {
  const { label, align = 'left', sortDir, onSort } = props
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement | null>(null)

  const active = useMemo(() => isActive(props), [props])

  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    function onEsc(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  return (
    <th
      className={`text-${align} text-xs font-semibold text-ink-muted uppercase tracking-wider px-4 py-2.5 bg-sunken border-b border-line whitespace-nowrap select-none`}
    >
      <div ref={wrapRef} className={`relative inline-flex items-center gap-1 ${align === 'right' ? 'justify-end w-full' : ''}`}>
        <button
          type="button"
          onClick={onSort}
          className="hover:text-ink transition-colors flex items-center gap-1"
          title={onSort ? 'Clique para ordenar' : undefined}
        >
          <span>{label}</span>
          {onSort && (
            <span className={`text-[10px] ${sortDir ? 'opacity-100 text-brand-300' : 'opacity-30'}`}>
              {sortDir === 'desc' ? '▼' : '▲'}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className={`ml-0.5 px-1 rounded text-[10px] transition-colors ${
            active ? 'bg-brand-700/60 text-white' : 'text-ink-faint hover:text-ink hover:bg-sunken'
          }`}
          title="Filtrar"
          aria-label={`Filtrar ${label}`}
        >
          ▾
        </button>

        {open && (
          <div className={`absolute top-full mt-1 ${align === 'right' ? 'right-0' : 'left-0'} z-30`}>
            <FilterPopover {...props} onClose={() => setOpen(false)} />
          </div>
        )}
      </div>
    </th>
  )
}

function isActive(p: Props): boolean {
  if (p.kind === 'categorical') return p.selected.size > 0
  if (p.kind === 'numeric') return p.min != null || p.max != null
  return p.from != null || p.to != null
}

function FilterPopover(p: Props & { onClose: () => void }) {
  if (p.kind === 'categorical') return <CategoricalPopover {...p} />
  if (p.kind === 'numeric') return <NumericPopover {...p} />
  return <DatePopover {...p} />
}

function CategoricalPopover(p: CategoricalProps & { onClose: () => void }) {
  const [q, setQ] = useState('')
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return p.options
    return p.options.filter(o => o.toLowerCase().includes(needle))
  }, [p.options, q])

  function toggle(value: string) {
    const next = new Set(p.selected)
    if (next.has(value)) next.delete(value); else next.add(value)
    p.onChange(next)
  }

  function clear() {
    p.onChange(new Set())
  }

  return (
    <div className="w-64 bg-surface border border-line rounded-lg shadow-xl p-2">
      <div className="flex items-center justify-between mb-2 gap-2">
        <input
          autoFocus
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Buscar..."
          className="flex-1 bg-paper border border-line rounded px-2 py-1 text-xs text-ink placeholder:text-ink-faint"
        />
        <button
          onClick={clear}
          disabled={p.selected.size === 0}
          className="text-[10px] px-2 py-1 rounded bg-sunken hover:bg-line disabled:opacity-40 text-ink-muted"
        >
          Limpar
        </button>
      </div>
      <div className="max-h-60 overflow-auto normal-case">
        {filtered.length === 0 ? (
          <div className="text-xs text-ink-faint px-2 py-3 text-center">Sem opções</div>
        ) : (
          filtered.map(opt => {
            const checked = p.selected.has(opt)
            return (
              <label
                key={opt}
                className="flex items-center gap-2 px-2 py-1.5 hover:bg-sunken rounded cursor-pointer text-sm text-ink-muted"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(opt)}
                  className="accent-brand-500"
                />
                <span className="truncate" title={opt}>{opt}</span>
              </label>
            )
          })
        )}
      </div>
    </div>
  )
}

function NumericPopover(p: NumericProps & { onClose: () => void }) {
  // Estado local pra digitação sem disparar refilter a cada tecla; aplicação só no blur/enter.
  // Reset externo (Limpar filtros) força remount via `key` no parent → estado reseta sozinho.
  const [minStr, setMinStr] = useState(p.min == null ? '' : String(p.min))
  const [maxStr, setMaxStr] = useState(p.max == null ? '' : String(p.max))

  function apply() {
    const min = minStr.trim() === '' ? null : Number(minStr)
    const max = maxStr.trim() === '' ? null : Number(maxStr)
    p.onChange(Number.isFinite(min as number) ? min : null, Number.isFinite(max as number) ? max : null)
  }

  function clear() {
    setMinStr(''); setMaxStr('')
    p.onChange(null, null)
  }

  return (
    <div className="w-56 bg-surface border border-line rounded-lg shadow-xl p-3 normal-case">
      <div className="text-[10px] text-ink-muted uppercase tracking-wider mb-2">Faixa</div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          step={p.step ?? 1}
          value={minStr}
          onChange={e => setMinStr(e.target.value)}
          onBlur={apply}
          onKeyDown={e => { if (e.key === 'Enter') apply() }}
          placeholder="mín"
          className="w-full bg-paper border border-line rounded px-2 py-1 text-sm text-ink placeholder:text-ink-faint tabular-nums"
        />
        <span className="text-ink-faint text-xs">a</span>
        <input
          type="number"
          step={p.step ?? 1}
          value={maxStr}
          onChange={e => setMaxStr(e.target.value)}
          onBlur={apply}
          onKeyDown={e => { if (e.key === 'Enter') apply() }}
          placeholder="máx"
          className="w-full bg-paper border border-line rounded px-2 py-1 text-sm text-ink placeholder:text-ink-faint tabular-nums"
        />
      </div>
      <div className="flex justify-end mt-2">
        <button
          onClick={clear}
          disabled={p.min == null && p.max == null}
          className="text-[10px] px-2 py-1 rounded bg-sunken hover:bg-line disabled:opacity-40 text-ink-muted"
        >
          Limpar
        </button>
      </div>
    </div>
  )
}

function DatePopover(p: DateProps & { onClose: () => void }) {
  function apply(field: 'from' | 'to', value: string) {
    const v = value || null
    if (field === 'from') p.onChange(v, p.to)
    else p.onChange(p.from, v)
  }

  function clear() {
    p.onChange(null, null)
  }

  return (
    <div className="w-60 bg-surface border border-line rounded-lg shadow-xl p-3 normal-case">
      <div className="text-[10px] text-ink-muted uppercase tracking-wider mb-2">Período</div>
      <label className="block mb-2">
        <span className="text-[10px] text-ink-faint">De</span>
        <input
          type="date"
          value={p.from ?? ''}
          onChange={e => apply('from', e.target.value)}
          className="w-full bg-paper border border-line rounded px-2 py-1 text-sm text-ink"
        />
      </label>
      <label className="block">
        <span className="text-[10px] text-ink-faint">Até</span>
        <input
          type="date"
          value={p.to ?? ''}
          onChange={e => apply('to', e.target.value)}
          className="w-full bg-paper border border-line rounded px-2 py-1 text-sm text-ink"
        />
      </label>
      <div className="flex justify-end mt-2">
        <button
          onClick={clear}
          disabled={p.from == null && p.to == null}
          className="text-[10px] px-2 py-1 rounded bg-sunken hover:bg-line disabled:opacity-40 text-ink-muted"
        >
          Limpar
        </button>
      </div>
    </div>
  )
}
